import React, { useState } from 'react';
import { submitQuestion, submitBug } from '../Helpers/firebaseHelpers';
import { useAuthUser } from './useAuthUser';

const Help = () =>
{
    const {userAuth , loading} = useAuthUser();
    const [currentView, setCurrentView] = useState('main'); // 'main', 'question', 'bug'
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Question form state
    const [questionData, setQuestionData] = useState({
        subject: '',
        description: ''
    });

    // Bug report form state
    const [bugData, setBugData] = useState({
        title: '',
        description: '',
        severity: 'medium'
    });

    const handleQuestionSubmit = async () =>
    {
        if (!questionData.subject.trim() || !questionData.description.trim())
        {
            alert("Please fill in all fields");
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await submitQuestion (
            {
                userName: userAuth.displayName,
                subject: questionData.subject,
                description: questionData.description
            });

            if (result.success)
            {
                alert("Question submitted successfully! We'll get back to you soon.");
                setQuestionData({ subject: '', description: '' });
                setCurrentView('main');
            } else 
            {
                alert("Failed to submit question: " + result.error);
            }
        } catch (error) 
        {
            console.error("Error submitting question:", error);
            alert("An error occurred while submitting your question.");
        }

        setIsSubmitting(false);
    };

    const handleBugSubmit = async () => 
    {
        if (!bugData.title.trim() || !bugData.description.trim()) 
        {
            alert("Please fill in all fields");
            return;
        }

        setIsSubmitting(true);

        try 
        {
            const result = await submitBug({
                userName: userAuth.displayName,
                title: bugData.title,
                description: bugData.description,
                severity: bugData.severity,
                browserInfo: navigator.userAgent,
                pageUrl: window.location.href
            });

            if (result.success)
            {
                alert("Bug report submitted successfully! Thank you for helping us improve.");
                setBugData({ title: '', description: '', severity: 'medium' });
                setCurrentView('main');
            } else
            {
                alert("Failed to submit bug report: " + result.error);
            }
        } catch (error)
        {
            console.error("Error submitting bug report:", error);
            alert("An error occurred while submitting your bug report.");
        }

        setIsSubmitting(false);
    };

    if (currentView === 'question')
    {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <button 
                    onClick={() => setCurrentView('main')}
                    className="mb-4 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                    ← Back
                </button>

                <h2 className="text-3xl font-bold mb-6">Ask a Question</h2>
                
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="mb-6">
                        <label className="block text-lg font-semibold mb-2">
                            Subject *
                        </label>
                        <input
                            type="text"
                            value={questionData.subject}
                            onChange={(e) => setQuestionData({...questionData, subject: e.target.value})}
                            placeholder="Brief summary of your question"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-lg font-semibold mb-2">
                            Description *
                        </label>
                        <textarea
                            value={questionData.description}
                            onChange={(e) => setQuestionData({...questionData, description: e.target.value})}
                            placeholder="Describe your question in detail..."
                            className="w-full p-4 border border-gray-300 rounded-lg h-40 resize-none focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    <button
                        onClick={handleQuestionSubmit}
                        disabled={isSubmitting}
                        className={`w-full py-3 rounded-lg text-lg font-medium ${
                            isSubmitting 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Question'}
                    </button>
                </div>
            </div>
        );
    }

    if (currentView === 'bug')
    {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <button 
                    onClick={() => setCurrentView('main')}
                    className="mb-4 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                    ← Back
                </button>

                <h2 className="text-3xl font-bold mb-6">Report a Bug</h2>
                
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="mb-6">
                        <label className="block text-lg font-semibold mb-2">
                            Bug Title *
                        </label>
                        <input
                            type="text"
                            value={bugData.title}
                            onChange={(e) => setBugData({...bugData, title: e.target.value})}
                            placeholder="Short description of the bug"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-lg font-semibold mb-2">
                            Severity
                        </label>
                        <select
                            value={bugData.severity}
                            onChange={(e) => setBugData({...bugData, severity: e.target.value})}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                        >
                            <option value="low">Low - Minor inconvenience</option>
                            <option value="medium">Medium - Affects functionality</option>
                            <option value="high">High - Major issue</option>
                            <option value="critical">Critical - System breaking</option>
                        </select>
                    </div>

                    <div className="mb-6">
                        <label className="block text-lg font-semibold mb-2">
                            Description *
                        </label>
                        <textarea
                            value={bugData.description}
                            onChange={(e) => setBugData({...bugData, description: e.target.value})}
                            placeholder="Describe the bug, steps to reproduce, and expected vs actual behavior..."
                            className="w-full p-4 border border-gray-300 rounded-lg h-40 resize-none focus:border-blue-500 focus:outline-none"
                        />
                    </div>

                    <button
                        onClick={handleBugSubmit}
                        disabled={isSubmitting}
                        className={`w-full py-3 rounded-lg text-lg font-medium ${
                            isSubmitting 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <h2 className="text-3xl font-bold mb-6">Help & Support</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div 
                    onClick={() => setCurrentView('question')}
                    className=
                    {`
                        bg-white hover:bg-blue-200 
                        transition-[background-color box-shadow] duration-500 ease-in-out
                        rounded-lg shadow-lg p-6 cursor-pointer border-2 border-blue-500
                    `}
                >
                    <div className="text-4xl mb-4">❓</div>
                    <h3 className="text-2xl font-semibold mb-2">Ask a Question</h3>
                    <p className="text-gray-600">
                        Have a question about the platform? We're here to help!
                    </p>
                </div>

                <div 
                    onClick={() => setCurrentView('bug')}
                    className=
                    {`
                        bg-white hover:bg-red-200 
                        transition-[background-color box-shadow] duration-500 ease-in-out
                        rounded-lg shadow-lg p-6 cursor-pointer border-2 border-red-500
                    `}  
                >
                    <div className="text-4xl mb-4">🐞</div>
                    <h3 className="text-2xl font-semibold mb-2">Report a Bug</h3>
                    <p className="text-gray-600">
                        Found something not working? Let us know so we can fix it.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Help;